import { useEffect, useMemo, useState } from "react";
import { PortalPage, Spinner } from "@/components/PortalPage";
import { api } from "@/lib/api";

const when = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
const Stat = ({ label, value, sub, testId }) => (
  <div className="rounded-2xl border border-[#333] bg-[#1A1A1A] p-4" data-testid={testId}>
    <p className="text-[10px] font-extrabold tracking-[1.5px] text-[var(--accent)]">{label}</p>
    <p className="mt-1 text-2xl font-extrabold">{value}</p>
    {sub && <p className="text-[11px] text-neutral-400">{sub}</p>}
  </div>
);
const FILTERS = [["all", "Everyone"], ["not_seen", "Haven't seen What's New"], ["not_used", "Haven't used builders"], ["used", "Using builders"]];

const Row = ({ r }) => (
  <tr className="border-t border-[#262626]" data-testid={`qbt-row-${r.id}`}>
    <td className="py-2.5 pr-3"><p className="font-bold">{r.name}</p><p className="text-[11px] text-neutral-400">{r.email}</p><p className="text-[11px] text-neutral-400">{r.role === "franchisee" ? "Owner" : `Staff · ${r.location || "—"}`}{!r.builder_access && " · no builder access"}</p></td>
    <td className="pr-3">{r.saw_whats_new ? <span className="text-[var(--accent)]" data-testid={`qbt-seen-${r.id}`}>Yes · {when(r.saw_at)}</span> : <span className="text-[#F59E0B]" data-testid={`qbt-seen-${r.id}`}>Not yet</span>}</td>
    <td className="pr-3 text-center">{r.quotes_calculated}</td>
    <td className="pr-3 text-center">{r.ai_scans}</td>
    <td className="pr-3 text-center">{r.quotes_saved}</td>
    <td className="pr-3 text-center">{r.patches_saved}</td>
    <td className="pr-3 text-center">{r.photos_videos}</td>
    <td className="text-right" data-testid={`qbt-last-${r.id}`}>{r.last_used ? when(r.last_used) : <span className="text-neutral-500">Never</span>}</td>
  </tr>
);

export default function QuoteBuilderTracker() {
  const [d, setD] = useState(null);
  const [f, setF] = useState("all");
  const [q, setQ] = useState("");
  const [err, setErr] = useState(null);
  useEffect(() => { api("/admin/quote-builder-tracker").then(setD).catch((e) => setErr(e.message)); }, []);
  const rows = useMemo(() => (d?.rows || []).filter((r) => (f === "all" || (f === "not_seen" && !r.saw_whats_new) || (f === "not_used" && !r.last_used) || (f === "used" && r.last_used))
    && `${r.name} ${r.email} ${r.location}`.toLowerCase().includes(q.toLowerCase())), [d, f, q]);
  return (
    <PortalPage eyebrow="CORPORATE ADMIN" title="Quote Builder Adoption" testId="qb-tracker-page" width="max-w-5xl">
      {err ? <p className="rounded-xl border border-[#EF4444] p-4 text-sm text-[#EF4444]" data-testid="qbt-error">Couldn't load the tracker: {err}</p> : !d ? <Spinner /> : <>
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="SAW WHAT'S NEW" value={`${d.saw_whats_new} / ${d.total}`} sub={`Quote Builder card (v${d.launch_version})`} testId="qbt-stat-seen" />
          <Stat label="USED THE BUILDERS" value={`${d.used_builders} / ${d.total}`} sub="Calculated, scanned, saved a quote or patch" testId="qbt-stat-used" />
          <Stat label="NOT STARTED" value={d.total - d.used_builders} sub="Good people to nudge" testId="qbt-stat-not-started" />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {FILTERS.map(([k, l]) => <button key={k} onClick={() => setF(k)} data-testid={`qbt-filter-${k}`} className={`rounded-full border px-3 py-1.5 text-xs font-bold ${f === k ? "border-[var(--accent)] text-[var(--accent)]" : "border-[#444] text-white"}`}>{l}</button>)}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name" data-testid="qbt-search" className="ml-auto rounded-full border border-[#444] bg-[#121212] px-3 py-1.5 text-xs text-white focus:outline-none" />
        </div>
        <div className="mt-3 overflow-x-auto rounded-2xl border border-[#333] bg-[#141414] p-3">
          <table className="w-full min-w-[760px] text-left text-xs" data-testid="qbt-table">
            <thead className="text-[10px] font-extrabold tracking-[1px] text-neutral-400"><tr>
              <th className="pb-2">FRANCHISEE / STAFF</th><th className="pb-2">SAW WHAT'S NEW</th><th className="pb-2 text-center">QUOTES</th><th className="pb-2 text-center">AI SCANS</th>
              <th className="pb-2 text-center">SAVED</th><th className="pb-2 text-center">PATCHES</th><th className="pb-2 text-center">PHOTOS/VIDEOS</th><th className="pb-2 text-right">LAST USED</th>
            </tr></thead>
            <tbody>{rows.map((r) => <Row key={r.id} r={r} />)}</tbody>
          </table>
          {!rows.length && <p className="py-6 text-center text-xs text-neutral-400" data-testid="qbt-empty">Nobody matches this filter.</p>}
        </div>
        <p className="mt-3 text-[11px] text-neutral-500" data-testid="qbt-note">{d.tracking_since} The announcement email had no open tracking. Practice accounts are left out.</p>
      </>}
    </PortalPage>
  );
}
